import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO'; 
import {
  Search,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Grape,
  Utensils,
  Flower2
} from 'lucide-react';

import PostCard from '../components/post/PostCard';

// Carousel Images (React Managed)
import slide1 from '../assets/carousel/01.jpg';
import slide2 from '../assets/carousel/02.jpg';
import slide3 from '../assets/carousel/03.jpg';
import slide4 from '../assets/carousel/04.jpg';
import slide5 from '../assets/carousel/05.jpg';

export default function CategoryPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);

  const categoryTitle = t(`category_page.titles.${slug}`) || t('category_page.titles.default');
  const categoryDesc = t(`category_page.seo_description.${slug}`) || t('homepage.seo_description');

  const localSlides = useMemo(() => [
    { 
      url: slide1, 
      title: t('homepage.slides.s1_title'), 
      sub: t('homepage.slides.s1_sub'),
      caption: currentLang === 'zh' ? '河畔葡萄园中的城堡酒庄' : 'A riverside chateau surrounded by golden vineyards',
      alt: currentLang === 'zh' ? '河畔葡萄园中的城堡酒庄' : 'A riverside chateau surrounded by golden vineyards'
    },
    { 
      url: slide2, 
      title: t('homepage.slides.s2_title'), 
      sub: t('homepage.slides.s2_sub'),
      caption: currentLang === 'zh' ? '阳光下的欧洲老街与露天酒馆' : 'A sun-drenched European street lined with outdoor wine cafes',
      alt: currentLang === 'zh' ? '阳光下的欧洲老街与露天酒馆' : 'A sun-drenched European street lined with outdoor wine cafes'
    },
    { 
      url: slide3, 
      title: t('homepage.slides.s3_title'), 
      sub: t('homepage.slides.s3_sub'),
      caption: currentLang === 'zh' ? '地中海露台的海景午餐' : 'Mediterranean terrace dining with a stunning sea view',
      alt: currentLang === 'zh' ? '地中海露台的海景午餐' : 'Mediterranean terrace dining with a stunning sea view'
    },
    { 
      url: slide4, 
      title: t('homepage.slides.s4_title'), 
      sub: t('homepage.slides.s4_sub'),
      caption: currentLang === 'zh' ? '日落时分色彩斑斓的港口晚宴' : 'Waterfront dining at sunset in a colorful harbor',
      alt: currentLang === 'zh' ? '日落时分色彩斑斓的港口晚宴' : 'Waterfront dining at sunset in a colorful harbor'
    },
    { 
      url: slide5, 
      title: t('homepage.slides.s5_title'), 
      sub: t('homepage.slides.s5_sub'),
      caption: currentLang === 'zh' ? '藏满酒桶与酒瓶的古朴酒窖' : 'A rustic underground wine cellar filled with barrels and bottles',
      alt: currentLang === 'zh' ? '藏满酒桶与酒瓶的古朴酒窖' : 'A rustic underground wine cellar filled with barrels and bottles'
    }
  ], [t, currentLang]);

  const categoryNav = useMemo(() => [
    { name: t('categories.learn-discover'), path: '/category/learn-discover', icon: BookOpen },
    { name: t('categories.region-grape'), path: '/category/region-grape', icon: Grape },
    { name: t('categories.foodpairing-lifestyle'), path: '/category/foodpairing-lifestyle', icon: Utensils },
    { name: t('categories.health-beauty'), path: '/category/health-beauty', icon: Flower2 }
  ], [t]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === localSlides.length - 1 ? 0 : prev + 1));
  }, [localSlides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? localSlides.length - 1 : prev - 1));
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  // --- STATIC DATA FETCHING LOGIC ---
  useEffect(() => {
    const fetchCategoryPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data.json');
        if (!response.ok) throw new Error('Failed to load posts');
        const allPosts = await response.json();

        // Map Chinese Category names from JSON to English URL Slugs
        const categoryMap = {
          '学习与探索': 'learn-discover',
          '产区和葡萄': 'region-grape',
          '美酒与生活': 'foodpairing-lifestyle',
          '健康与美容': 'health-beauty'
        };

        let filtered = allPosts.filter(post => {
          // 1. Filter by mapped category slug
          const postSlug = categoryMap[post.category];
          const categoryMatch = postSlug === slug;
          
          // 2. Filter by search query (Title or Excerpt)
          const searchLower = searchQuery.toLowerCase();
          const title = (post.title?.[currentLang] || post.title?.['en'] || "").toLowerCase();
          const excerpt = (post.excerpt?.[currentLang] || post.excerpt?.['en'] || "").toLowerCase();
          const searchMatch = title.includes(searchLower) || excerpt.includes(searchLower);
          
          return categoryMatch && searchMatch;
        });

        // 3. Sorting logic
        filtered.sort((a, b) => {
          if (sortBy === 'views') {
            return (b.view_count || 0) - (a.view_count || 0);
          } else {
            // Your data.json uses YYYY-MM-DD strings
            return new Date(b.date) - new Date(a.date);
          }
        });

        setPosts(filtered);
      } catch (err) {
        console.error("Error loading category data:", err);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryPosts();
  }, [slug, sortBy, searchQuery, currentLang]);

  // ===== 新增：CollectionPage Schema =====
  const collectionSchema = useMemo(() => {
    if (!posts.length || typeof window === 'undefined') return null;

    // 获取分类的中文名称用于显示
    const categoryNameMap = {
      'learn-discover': currentLang === 'zh' ? '学习与探索' : 'Learn & Discover',
      'region-grape': currentLang === 'zh' ? '产区和葡萄' : 'Region & Grape',
      'foodpairing-lifestyle': currentLang === 'zh' ? '美酒与生活' : 'Food & Lifestyle',
      'health-beauty': currentLang === 'zh' ? '健康与美容' : 'Health & Beauty'
    };

    const displayCategoryName = categoryNameMap[slug] || categoryTitle;

    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": displayCategoryName,
      "description": categoryDesc,
      "url": `${window.location.origin}/category/${slug}`,
      "mainEntity": {
        "@type": "ItemList",
        "name": displayCategoryName,
        "description": categoryDesc,
        "numberOfItems": posts.length,
        "itemListElement": posts.slice(0, 10).map((post, index) => ({ // 限制前10个，避免过大
          "@type": "ListItem",
          "position": index + 1,
          "url": `${window.location.origin}/post/${post.slug}`,
          "name": currentLang === 'zh' ? post.title.zh : post.title.en,
          "description": post.excerpt?.[currentLang] || post.excerpt?.['en'] || '',
          "image": post.cover_image || '',
          "datePublished": post.date
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": currentLang === 'zh' ? '首页' : 'Home',
            "item": window.location.origin
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": displayCategoryName,
            "item": `${window.location.origin}/category/${slug}`
          }
        ]
      }
    };
  }, [posts, slug, categoryTitle, categoryDesc, currentLang]);

  return (
    <main key={i18n.language} className="flex flex-col bg-cream min-h-screen overflow-x-hidden">
      <SEO 
        title={categoryTitle} 
        description={categoryDesc}
        slug={`category/${slug}`}
        type="collection"
        keywords={currentLang === 'zh' 
          ? `${categoryTitle}, 葡萄酒专题, 欧洲精品酒庄` 
          : `${categoryTitle}, Wine specialities, European estates`}
        aiSummary={currentLang === 'zh' 
          ? `深入探索${categoryTitle}系列文章。EuroWineTrip为您提供专业的品鉴笔记与产区故事。` 
          : `Explore our ${categoryTitle} collection. Expert tasting notes and regional stories from EuroWineTrip.`}
      />
      
      {/* ===== 新增：CollectionPage 结构化数据 ===== */}
      {collectionSchema && (
        <script type="application/ld+json">
          {JSON.stringify(collectionSchema)}
        </script>
      )}
      
      <div className="h-20 md:h-24 w-full" />

      {/* --- CAROUSEL SECTION --- */}
      <section className="relative w-screen h-[450px] md:h-[700px] bg-dark-olive shadow-2xl overflow-hidden mt-4 md:mt-8 left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] group">
        <div className="relative w-full h-full">
          <figure className="w-full h-full m-0 p-0 relative">
            <img
              src={localSlides[currentSlide].url}
              alt={localSlides[currentSlide].alt}
              className="w-full h-full object-cover opacity-80 transition-opacity duration-1000 scale-105"
            />
            <figcaption className="absolute bottom-10 left-1/2 -translate-x-1/2 z-40 bg-black/40 backdrop-blur-md px-5 py-1.5 rounded-full text-[10px] md:text-xs text-white/80 font-serif italic tracking-wide opacity-0 group-hover:opacity-100 transition-all duration-500 border border-white/10 shadow-lg">
              {localSlides[currentSlide].caption}
            </figcaption>
          </figure>

          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />
          
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6 pointer-events-none">
            <h2 className="text-5xl md:text-9xl font-serif italic mb-4 drop-shadow-2xl tracking-tight">
              {localSlides[currentSlide].title}
            </h2>
            <p className="text-xs md:text-lg uppercase tracking-[0.6em] mb-10 opacity-90 font-light">
              {localSlides[currentSlide].sub}
            </p>
          </div>

          <button onClick={prevSlide} className="absolute left-2 md:left-10 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-all z-30">
            <ChevronLeft className="w-8 h-8 md:w-16 md:h-16" strokeWidth={1} />
          </button>
          <button onClick={nextSlide} className="absolute right-2 md:right-10 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-all z-30">
            <ChevronRight className="w-8 h-8 md:w-16 md:h-16" strokeWidth={1} />
          </button>
        </div>
      </section>

      {/* --- CATEGORY BLOCKS --- */}
      <nav className="sticky top-0 z-40 bg-cream/95 backdrop-blur-md py-6 md:py-10 border-b border-olive/10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {categoryNav.map((cat) => (
              <Link
                key={cat.path}
                to={cat.path}
                className={`bg-olive h-28 md:h-32 shadow-lg flex flex-col items-center justify-center p-4 text-center group transition-all duration-500 rounded-2xl md:rounded-[2rem] text-white hover:bg-dark-olive hover:scale-[1.03] ${cat.path.includes(slug) ? 'ring-2 ring-wine ring-offset-2 bg-dark-olive' : ''}`}
              >
                <cat.icon
                  className="w-7 h-7 md:w-8 md:h-8 mb-2 md:mb-3 transition-colors duration-300 group-hover:text-[#800020]"
                  strokeWidth={1.5}
                />
                <span className="font-bold uppercase tracking-widest text-[13px] md:text-[11px] leading-tight transition-colors duration-300 group-hover:text-[#800020]">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* --- NAVIGATION & FILTERS BAR --- */}
      <div className="container mx-auto px-4 md:px-6 mt-12">
        <Link
          to="/"
          className="flex items-center gap-2 text-olive font-bold tracking-widest text-sm md:text-xs mb-8 hover:text-[#800020] hover:translate-x-[-4px] transition-all duration-300"
        >
          <ArrowLeft size={18} /> {t('category_page.back_home')}
        </Link>

        <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 border-b border-olive/20 pb-8">
          <h1 className="text-xl md:text-3xl font-serif text-olive italic tracking-tight whitespace-nowrap">
            {categoryTitle}
          </h1>

          <div className="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
            <div className="flex bg-olive/10 p-1.5 rounded-full border border-olive/30 shadow-sm" role="group">
              <button
                onClick={() => setSortBy('created_at')}
                className={`px-8 py-2.5 rounded-full text-[13px] md:text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 ${
                  sortBy === 'created_at' ? 'bg-olive text-white shadow-md' : 'text-olive hover:bg-olive/5'
                }`}
              >
                {t('homepage.recent')}
              </button>
              <button
                onClick={() => setSortBy('views')}
                className={`px-8 py-2.5 rounded-full text-[13px] md:text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 ${
                  sortBy === 'views' ? 'bg-olive text-white shadow-md' : 'text-olive hover:bg-olive/5'
                }`}
              >
                {t('homepage.popular')}
              </button>
            </div>

            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder={t('category_page.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent border border-olive/30 rounded-full py-2.5 px-10 text-sm focus:outline-none focus:border-olive text-olive placeholder:text-olive/50"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-olive/50" size={18} />
            </div>
          </div>
        </header>
      </div>

      {/* --- POST GRID --- */}
      <section className="container mx-auto px-4 md:px-6 mb-24 mt-12">
        {loading ? (
          <div className="h-64 flex justify-center items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-olive"></div>
          </div>
        ) : posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-16">
            {posts.map(post => (
              <article key={post.slug}>
                <PostCard post={post} />
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-olive opacity-60 italic">
            {t('category_page.no_posts')}
          </div>
        )}
      </section>
    </main>
  );
}